import React from "react";
import _ from "underscore";
import { isServer } from "../../core/detection";
import { useScript } from "../helpers/useScriptHook";
import { useStyle } from "../helpers/useStyleHook";
import { Loader } from "../../core/atoms/atoms";
import { ErrorBoundary } from "../components/errorBoundary";
import { accessToken, styleId } from "@travellocal/utils/mapboxHelper"

interface MapboxMapProps {
  mapId?: string;
  locationSelected?: (latlng: L.LatLng, zoomLevel: number) => void;
  zoomUpdated?: (zoomLevel: number) => void;
  markerLocation?: IGeoLocation | IGeoLocation[];
  readonly?: boolean;
  height?: number;
  width?: number;
  hideMarkers?: boolean;
  zoomLevel?: number;
  className?: string;
}

export const MapboxMap: React.FC<MapboxMapProps> = (props) => {
  if (isServer()) {
    return null;
  }

  let marker: L.Marker;
  let geocoderControl: L.mapbox.GeocoderControl;

  const [scriptLoaded, scriptLoadError] = useScript(
    "https://api.mapbox.com/mapbox.js/v2.3.0/mapbox.js"
  );

  const [styleLoaded, styleLoadError] = useStyle(
    "https://api.mapbox.com/mapbox.js/v2.3.0/mapbox.css"
  );

  // The DOM element Mapbox renders the map into
  const mapContainer = React.useRef(null);

  // A reference to the L.map object
  const mapObject = React.useRef(null);

  // Used to show and hide the <Loader />
  const [mapFilesLoaded, setMapFilesLoaded] = React.useState(false);

  // Used to add a single marker to the map
  const setLocation = (
    latlng: L.LatLng,
    map: L.Map,
    dontUpdateModel?: boolean
  ) => {
    if (!props.hideMarkers) {
      if (marker) {
        map.removeLayer(marker);
      }

      // Add a new marker to the map
      L.marker(L.latLng(latlng)).addTo(map);
    }
    if (!dontUpdateModel) {
      if (props.locationSelected) {
        props.locationSelected(latlng, map.getZoom());
      }
    }
  };

  // Used to set multiple markers and draw a line on the map
  const setMultipleLocations = (locations: IGeoLocation[], map: L.Map) => {
    if (locations.length === 0) {
      return;
    }

    const bounds = [];

    _.each(locations, (location) => {
      if (location.latitude && location.longitude) {
        const ll = L.latLng(location.latitude, location.longitude);
        bounds.push([location.latitude, location.longitude]);

        // Add a new marker to the map
        L.marker(L.latLng(ll)).addTo(map);
      }
    });

    // Draw a line between the markers
    L.polyline(bounds, {
      color: "#3498db",
      dashArray: "5, 5",
      weight: 3,
      opacity: 1,
    }).addTo(map);

    map.fitBounds(bounds);
  };

  // Update mapFilesLoaded when scripts are loading
  // Will only be true when the condition is met
  React.useEffect(() => {
    setMapFilesLoaded(scriptLoaded && styleLoaded && !scriptLoadError && !styleLoadError);
  }, [scriptLoaded, styleLoaded, scriptLoadError, styleLoadError]);

  // On initial mount/render, this will exit early
  // Runs each time mapFilesLoaded is updated
  React.useEffect(() => {
    // Don't try and run anything if the script has not loaded
    // Or L (set by MapBox script) does not exist
    if (!mapFilesLoaded || !window.L) {
      return;
    }

    let centralLocation: IGeoLocation;

    if (Array.isArray(props.markerLocation)) {
      centralLocation =
        props.markerLocation.length > 0 ? props.markerLocation[0] : null;
    }
    else {
      centralLocation = props.markerLocation;
    }

    // Determine where to centre the map
    const initialView =
      props.markerLocation && centralLocation && centralLocation.latitude
        ? L.latLng(centralLocation.latitude, centralLocation.longitude)
        : L.latLng(51, 0);

    // Create a new map
    mapObject.current = L.map(mapContainer.current, null).setView(
      initialView,
      props.zoomLevel || 7
    );

    mapObject.current.attributionControl.removeFrom(mapObject.current);
    mapObject.current.scrollWheelZoom.disable();

    L.mapbox.accessToken = accessToken;

    L.mapbox
      .styleLayer(`mapbox://styles/travellocal/${styleId}`)
      .addTo(mapObject.current);

    if (!props.readonly) {
      geocoderControl = L.mapbox.geocoderControl("mapbox.places", {
        autocomplete: true,
      });

      mapObject.current.addControl(geocoderControl);

      mapObject.current.on("click", (e) => {
        const ll = e.latlng;
        setLocation(ll, mapObject.current);
      });

      if (props.zoomUpdated) {
        mapObject.current.on("zoomend", () => {
          props.zoomUpdated(mapObject.current.getZoom());
        });
      }

      geocoderControl.on("select", (e) => {
        const latlng = [e.feature.center[1], e.feature.center[0]];
        setLocation(L.latLng(latlng), mapObject.current);
      });
    }
    else {
      mapObject.current.zoomControl.removeFrom(mapObject.current);
      mapObject.current.dragging.disable();
      mapObject.current.touchZoom.disable();
      mapObject.current.doubleClickZoom.disable();
      mapObject.current.keyboard.disable();
      // Disable tap handler, if present.
      if (mapObject.current.tap) {
        mapObject.current.tap.disable();
      }
    }

    if (Array.isArray(props.markerLocation)) {
      setMultipleLocations(props.markerLocation, mapObject.current);
    }
    else if (
      centralLocation.latitude != null &&
      centralLocation.longitude != null
    ) {
      setLocation(
        L.latLng(centralLocation.latitude, centralLocation.longitude),
        mapObject.current,
        true
      );
    }
  }, [mapFilesLoaded]);

  const style = {
    height: props.height ? `${props.height}px` : "500px",
    width: props.width ? `${props.width}px` : "100%",
  };

  if (scriptLoadError || styleLoadError) {
    throw new Error(`Unable to load map: ${scriptLoadError ? 'script' : 'style'} load error`)
    return;
  }

  return (
    <div className={props.className}>
      <div
        ref={mapContainer}
        style={{
          ...style,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          overflow: "hidden",
        }}
      >
        {/* Loader is hidden when the map scripts are ready and no loading errors */}
        {!mapFilesLoaded && !scriptLoadError && !styleLoadError && (
          <div
            style={{
              display: "flex",
              ...style,
            }}
          >
            <Loader />
          </div>
        )}
      </div>
    </div>
  );
};

// Wraps the <MapboxMap> in an error boundary
// in case the latter throws an error
export const MapWrapper: React.FC<MapboxMapProps> = (props) => {
  return (
    <ErrorBoundary>
      <MapboxMap {...props} />
    </ErrorBoundary>
  );
};
